import { useEffect, useRef, useState } from "react";

/** requestAnimationFrame-based count-up. CSS-only is preferred for visuals,
 *  but counters need a numeric value the user can read so we use rAF.
 *  Skips animation if `prefers-reduced-motion: reduce`. */
export function useCountUp(target, { duration = 900 } = {}) {
    const [v, setV] = useState(0);
    const last = useRef(target);
    useEffect(() => {
        if (typeof target !== "number" || Number.isNaN(target)) {
            setV(0);
            last.current = target;
            return;
        }
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduced) {
            setV(target);
            last.current = target;
            return;
        }
        const from = last.current ?? 0;
        const start = performance.now();
        let raf = 0;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const cur = Math.round(from + (target - from) * eased);
            setV(cur);
            if (t < 1) raf = requestAnimationFrame(tick);
            else last.current = target;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return v;
}

/** Display helper — formats currencies, large numbers, etc. */
export function CountUp({ value, prefix = "", suffix = "", decimals = 0, duration = 900, className = "" }) {
    const v = useCountUp(typeof value === "number" ? value : 0, { duration });
    const out = decimals > 0 ? v.toFixed(decimals) : v.toLocaleString();
    return <span className={className}>{prefix}{out}{suffix}</span>;
}
